import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye, faEyeSlash, faUser, faMotorcycle,
  faArrowLeft, faCircleExclamation
} from '@fortawesome/free-solid-svg-icons';
import { FcGoogle } from 'react-icons/fc';
import { BryButton, BryInput } from '../../components/UI/BryUI';
import PasswordStrengthBar from '../../components/UI/PasswordStrengthBar';
import { ROLES } from '../../constants';

const ROLE_OPTIONS = [
  {
    key: ROLES.PASSENGER,
    label: 'Soy Cliente',
    sublabel: 'Necesito transporte o delivery',
    icon: faUser
  },
  {
    key: ROLES.DRIVER,
    label: 'Soy Conductor',
    sublabel: 'Ofrezco servicios con mi vehículo',
    icon: faMotorcycle
  }
];

export const LandingAuthForm = ({
  mode, setMode, identifier, setIdentifier, name, setName,
  password, setPassword, confirmPassword, setConfirmPassword,
  selectedRole, setSelectedRole, showPassword, setShowPassword,
  loading, feedback, errors, handleSubmit, handleGoogleLogin, reset
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-4 rounded-4 shadow mx-auto"
      style={{
        backgroundColor: 'var(--bry-bg-primary, #F4F5F7)',
        border: '1px solid var(--bry-accent, #0EABB0)',
        maxWidth: '440px'
      }}
    >
      {/* Selector de modo Login / Register */}
      {mode !== 'recovery' && (
        <div className="d-flex rounded-3 p-1 mb-4" style={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}>
          <button
            type="button"
            className="flex-fill btn py-2 fw-bold text-center border-0 rounded-3 transition-all"
            style={
              mode === 'login'
                ? { backgroundColor: 'var(--bry-accent, #0EABB0)', color: '#ffffff' }
                : { color: '#64748b' }
            }
            onClick={() => reset('login')}
          >
            Iniciar Sesión
          </button>
          <button
            type="button"
            className="flex-fill btn py-2 fw-bold text-center border-0 rounded-3 transition-all"
            style={
              mode === 'register'
                ? { backgroundColor: 'var(--bry-accent, #0EABB0)', color: '#ffffff' }
                : { color: '#64748b' }
            }
            onClick={() => reset('register')}
          >
            Registrarse
          </button>
        </div>
      )}

      {/* Título de Recuperación */}
      {mode === 'recovery' && (
        <div className="mb-4">
          <button
            type="button"
            className="btn btn-link text-secondary p-0 text-decoration-none small mb-2"
            onClick={() => reset('login')}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Volver al Login
          </button>
          <h5 className="text-dark fw-bold">Recuperar Contraseña</h5>
          <p className="text-secondary small mb-0">
            Ingresa tu correo o teléfono registrado para enviarte un enlace de acceso.
          </p>
        </div>
      )}

      {/* Alerta de Feedback / Error */}
      {feedback && (
        <div
          className={`alert py-2 px-3 small rounded-3 mb-3 d-flex align-items-center gap-2 ${
            feedback.type === 'error' ? 'alert-danger' : 'alert-success'
          }`}
        >
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span>{feedback.message}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Campo Nombre (solo en registro) */}
        {mode === 'register' && (
          <div className="mb-3">
            <label className="form-label text-secondary small fw-semibold">Nombre Completo</label>
            <BryInput
              type="text"
              placeholder="Ej: Juan Pérez"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            {errors.name && <span className="text-danger extra-small">{errors.name}</span>}
          </div>
        )}

        {/* Campo Identificador (Correo / Teléfono) */}
        <div className="mb-3">
          <label className="form-label text-secondary small fw-semibold">Correo o Teléfono</label>
          <BryInput
            type="text"
            placeholder="correo@ejemplo.com o 04141234567"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
          />
          {errors.identifier && <span className="text-danger extra-small">{errors.identifier}</span>}
        </div>

        {/* Selector de Rol (Cliente / Conductor) en Registro */}
        {mode === 'register' && (
          <div className="mb-3">
            <label className="form-label text-secondary small fw-semibold">¿Cómo deseas usar Bryemma?</label>
            <div className="d-flex gap-2">
              {ROLE_OPTIONS.map((role) => {
                const isSelected = selectedRole === role.key;
                return (
                  <div
                    key={role.key}
                    onClick={() => setSelectedRole(role.key)}
                    className="flex-fill p-2 rounded-3 text-center border transition-all"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isSelected ? 'rgba(14, 171, 176, 0.12)' : '#f8fafc',
                      borderColor: isSelected ? 'var(--bry-accent, #0EABB0)' : '#cbd5e1',
                      color: isSelected ? 'var(--bry-accent, #0EABB0)' : '#334155'
                    }}
                  >
                    <FontAwesomeIcon icon={role.icon} style={{ color: isSelected ? 'var(--bry-accent, #0EABB0)' : 'inherit' }} className="mb-1 d-block mx-auto" />
                    <span className="fw-bold small d-block">{role.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Campos de Contraseña */}
        {mode !== 'recovery' && (
          <>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label text-secondary small fw-semibold mb-0">Contraseña</label>
                {mode === 'login' && (
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none p-0 extra-small"
                    style={{ color: 'var(--bry-accent, #0EABB0)' }}
                    onClick={() => reset('recovery')}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}
              </div>
              <div className="position-relative">
                <BryInput
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-secondary pe-3 border-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {mode === 'register' && <PasswordStrengthBar password={password} />}
              {errors.password && <span className="text-danger extra-small">{errors.password}</span>}
            </div>

            {mode === 'register' && (
              <div className="mb-3">
                <label className="form-label text-secondary small fw-semibold">Confirmar Contraseña</label>
                <BryInput
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                {errors.confirmPassword && <span className="text-danger extra-small">{errors.confirmPassword}</span>}
              </div>
            )}
          </>
        )}

        {/* Botón Principal de Envío */}
        <BryButton
          type="submit"
          variant="primary"
          className="w-100 py-2 fw-bold mt-2"
          isLoading={loading}
          style={{ backgroundColor: 'var(--bry-accent, #0EABB0)', color: '#ffffff', border: 'none' }}
        >
          {mode === 'login' && 'Ingresar a Bryemma'}
          {mode === 'register' && 'Crear Mi Cuenta'}
          {mode === 'recovery' && 'Enviar Enlace de Acceso'}
        </BryButton>
      </form>

      {/* Botón Login con Google */}
      {mode !== 'recovery' && (
        <div className="mt-3 text-center pt-3 border-top border-secondary border-opacity-25">
          <p className="text-secondary extra-small mb-2">O bien continúa con</p>
          <button
            type="button"
            className="btn w-100 d-flex align-items-center justify-content-center gap-2 py-2 rounded-3 text-dark border"
            style={{ backgroundColor: '#f8fafc', borderColor: '#cbd5e1', fontWeight: 600 }}
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            <FcGoogle size={20} /> Google
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default LandingAuthForm;
